import React, { useEffect, useRef, useState } from "react";
import * as PR from "../../prime-modules/index";
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { adminAuthActions } from "../../store/adminAuth";
import { resetFilters } from "../../store/orderFilters";
import { resetUserFilters } from "../../store/userFilters";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { getBrandName } from "../../GlobalConfig";

const AdminHeader = () => {
  const menu = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const appName = process.env?.REACT_APP_NAME;
  const isEnableEmailTemplates = process.env?.REACT_APP_ENVIRONMENT === 'dev';
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const cached = localStorage.getItem('sidebar-collapsed');
    return cached === null ? true : cached === 'true';
  });
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const userPermission = adminData?.permission;

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed);
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  }, [isCollapsed]);

  useEffect(() => {
    const currentVersion = process.env.REACT_APP_VERSION || '';
    const cachedVersion = localStorage.getItem('BO-VERSION');

    if (!currentVersion) {
      return;
    }

    if (cachedVersion && cachedVersion !== currentVersion) {
      localStorage.setItem('BO-VERSION', currentVersion);
      window.location.reload()
      return;
    }

    localStorage.setItem('BO-VERSION', currentVersion);
  }, []);

  const items = [
    {
      label: "Logout",
      icon: "pi pi-power-off",
      command: () => {
        dispatch(adminAuthActions.onLogout())
      }
    }
  ];

  const DATA2GO_VARIANTS = ["data2go", "data2go-iot"];
  const isData2GoApp = DATA2GO_VARIANTS.includes(appName?.toLowerCase());

  const itemTemplate = (item, options) => {
    return (
      <Link
        to={item.command ? '#' : item.url || '#'}
        className={`${options.className} custom-menu-item`}
        onClick={(e) => {
          if (options.onClick) {
            options.onClick(e);
          }
          if (item.command) {
            e.preventDefault();
            item.command();
          }
        }}
        data-pr-tooltip={isCollapsed ? item.label : ''}
        data-pr-position="right"
        data-pr-at="right+10 center"
      >
        <span className={`${options.iconClassName} ${item.icon}`}></span>
        <span className={options.labelClassName}>{item.label}</span>
      </Link>
    );
  };

  const menuItems = [
    {
      label: 'Dashboard',
      icon: 'pi pi-fw pi-th-large',
      command: () => { navigate('/dashboard') },
      className: (pathname === '/dashboard') ? 'active-menu' : '',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_REPORTS,
      template: itemTemplate
    },
    {
      label: 'Orders',
      icon: 'pi pi-fw pi-shopping-cart',
      command: () => { navigate('/orders') },
      className: (pathname.includes('/orders')) ? 'active-menu' : '',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_ORDERS,
      template: itemTemplate
    },
    {
      label: 'Customers',
      icon: 'pi pi-fw pi-user',
      command: () => { navigate('/users') },
      className: (pathname === '/users' || pathname.includes('/user-orders') || pathname.includes('/customer-details')) ? 'active-menu' : '',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_USERS,
      template: itemTemplate
    },
    {
      label: 'Affiliates',
      icon: 'pi pi-sitemap',
      command: () => { navigate('/affiliates') },
      className: (pathname === '/affiliates') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot',
      requiredPermission: PERMISSIONS.VIEW_AFFILIATES,
      template: itemTemplate
    },
    {
      label: 'eSIM Details',
      icon: 'pi pi-fw pi-users',
      command: () => { navigate('/esim-Details') },
      className: (pathname === '/' || pathname.includes('/esim-details') || pathname.includes('/esim-Details') || pathname.includes('/user-esims')) ? 'active-menu' : '',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_REPORTS,
      template: itemTemplate
    },
    {
      label: 'Wholesale Prices',
      icon: 'pi pi-fw pi-tags',
      command: () => { navigate('/wholesale-price-adjustment') },
      className: (pathname === '/wholesale-price-adjustment') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot' && (hasPermission(userPermission, PERMISSIONS.MANAGE_ADMINS)),
      requiredPermission: PERMISSIONS.VIEW_PRICES,
      template: itemTemplate
    },
    {
      label: 'Price Management',
      icon: 'pi pi-fw pi-dollar',
      command: () => { navigate('/price-adjustment') },
      className: (pathname === '/price-adjustment') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot',
      requiredPermission: PERMISSIONS.VIEW_PRICES,
      template: itemTemplate
    },
    {
      label: 'Device Types',
      icon: 'pi pi-mobile',
      command: () => { navigate('/device-types') },
      className: (pathname === '/device-types') ? 'active-menu' : '',
      enable: appName === 'elefone' || appName === 'briant' || appName === 'data2go-iot',
      requiredPermission: PERMISSIONS.MANAGE_CONFIG,
      template: itemTemplate
    },
    {
      label: 'Subscription Plans',
      icon: 'pi pi-fw pi-calendar',
      command: () => { navigate('/subscription-plans') },
      className: (pathname === '/subscription-plans') ? 'active-menu' : '',
      enable: appName === 'elefone' || appName === 'briant' || appName === 'data2go-iot',
      requiredPermission: PERMISSIONS.VIEW_PRICES,
      template: itemTemplate
    },
    {
      label: 'Discount Vouchers',
      icon: 'pi pi-percentage',
      command: () => { navigate('/vouchers') },
      className: (pathname === '/vouchers') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot',
      requiredPermission: [PERMISSIONS.VIEW_PROMOS, PERMISSIONS.MANAGE_PROMOS],
      template: itemTemplate
    },
    {
      label: 'Prepaid Vouchers',
      icon: 'pi pi-ticket',
      command: () => { navigate('/prepaid-retail-vouchers') },
      className: (pathname === '/prepaid-retail-vouchers') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot',
      requiredPermission: [PERMISSIONS.VIEW_PREPAID_VOUCHERS, PERMISSIONS.MANAGE_PREPAID_VOUCHERS],
      template: itemTemplate
    },
    {
      label: 'Push Notifications',
      icon: 'pi pi-envelope',
      command: () => { navigate('/push-notifications') },
      className: (pathname === '/push-notifications') ? 'active-menu' : '',
      enable: (appName !== 'data2go-iot' && appName !== 'esimple' && appName !== 'gopamwe'),
      requiredPermission: PERMISSIONS.VIEW_PUSH_NOTIFICATIONS,
      template: itemTemplate
    },
    {
      label: 'BO Users',
      icon: 'pi pi-users',
      command: () => { navigate('/admin-users') },
      className: (pathname === '/admin-users') ? 'active-menu' : '',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_ADMINS,
      template: itemTemplate
    },
    {
      label: 'Settings',
      icon: 'pi pi-fw pi-cog',
      command: () => { navigate('/settings') },
      className: (pathname === '/settings') ? 'active-menu' : '',
      enable: appName !== 'data2go-iot',
      requiredPermission: PERMISSIONS.MANAGE_CONFIG,
      template: itemTemplate
    },
    {
      label: 'KYC Countries',
      icon: 'pi pi-id-card',
      command: () => { navigate('/kyc-countries') },
      className: (pathname === '/kyc-countries') ? 'active-menu' : '',
      enable: appName === 'data2go',
      requiredPermission: PERMISSIONS.MANAGE_CONFIG,
      template: itemTemplate
    },
    {
      label: appName === 'data2go-iot' ? 'Language Translate' : 'CMS',
      icon: 'pi pi-fw pi-code',
      command: () => { navigate('/cms') },
      className: (pathname === '/cms') ? 'cms-menu active-menu' : 'cms-menu',
      enable: true,
      requiredPermission: PERMISSIONS.VIEW_CONTENT,
      template: itemTemplate
    },
    {
      label: 'Email Templates',
      icon: 'pi pi-envelope',
      command: () => { navigate('/email-templates') },
      className: (pathname === '/email-templates') ? 'active-menu' : '',
      enable: isEnableEmailTemplates,
      requiredPermission: PERMISSIONS.VIEW_EMAIL_TEMPLATES,
      template: itemTemplate
    },
  ].filter(m => m.enable && (!m.requiredPermission || hasPermission(userPermission, m.requiredPermission)));

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const toggleDesktopMenu = () => setIsCollapsed(!isCollapsed);

  const end = <div className="flex align-items-center logout-container">
    <span className="user-icon" onClick={() => {
      dispatch(adminAuthActions.onLogout());
      dispatch(resetFilters());
      dispatch(resetUserFilters());
    }}><i className="pi pi-power-off"></i></span>
    <PR.Button
      label="Logout"
      icon="pi pi-arrow-right"
      iconPos="right"
      className="logout-button"
      onClick={() => {
        dispatch(adminAuthActions.onLogout());
        dispatch(resetFilters());
        dispatch(resetUserFilters());
      }}
    />
    <PR.Menu model={items} popup ref={menu} />
  </div>;

  return (
    <>
      <Helmet>
        <title>{`${getBrandName(appName)} | Backoffice`}</title>
      </Helmet>
      <section className="admin-header-section">
        <div className="flex align-items-center justify-content-center w-full logo-toggle-container">
          <Link to="/" className="brand-logo">
            <PR.Image
              src={`/brands/logos/${appName || "data2go"}-logo.png`}
              loading="eager"
              alt={`${getBrandName(appName)} Logo`}
              onError={(e) => {
                e.currentTarget.src = "/brands/logos/data2go-logo.png";
              }}
              className={`${appName === "orzo" ? "orzo-logo" : ""} main-logo`}
            />
            <PR.Image
              src={`/brands/${appName || "data2go"}-icon.png`}
              loading="eager"
              alt={`${getBrandName(appName)} Logo`}
              onError={(e) => {
                e.currentTarget.src = "/brands/data2go-icon.png";
              }}
              className={`${appName === "orzo" ? "orzo-icon" : ""} main-icon`}
            />
          </Link>
          <PR.Button text className="menu-toggle md:hidden" onClick={toggleMenu}>
            <i className={`pi ${menuOpen ? "pi-times" : "pi-bars"}`}></i>
          </PR.Button>
          <PR.Button text className="desktop-menu-toggle hidden md:block" onClick={toggleDesktopMenu}>
            <i className={`pi ${isCollapsed ? "pi-bars" : "pi-bars"}`}></i>
          </PR.Button>
        </div>
      </section>
      <div className={`sidebar-wrapper ${isData2GoApp ? "sidebar-height" : ""}`}>
        <header className={`admin-header ${menuOpen ? "menu-show" : ""} ${isCollapsed ? "collapsed" : ""}`}>
          <PR.Menubar model={menuItems} end={end} className="w-12" />
          <PR.Tooltip target=".custom-menu-item" disabled={!isCollapsed} />
          {appName?.toLowerCase() !== "data2go" && (
            <a href="https://data2go.mobi/" target="_blank" rel="noreferrer">
              <div className="footer-logo">
                <p>Powered by</p>
                <PR.Image
                  src="/brands/logos/data2go-logo.png"
                  alt="data2go Logo"
                  className="bottom-logo main-logo"
                />
                <PR.Image
                  src="/brands/data2go-icon.png"
                  alt="data2go Icon"
                  className="bottom-logo main-icon"
                />
              </div>
            </a>
          )}
        </header>
      </div>
    </>
  )

}

export default AdminHeader
